import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  employerApi, ApiRequestError,
  type EmployerSignup, type EmployerUser, type OrgMembership,
} from '@pinchcrew/core'

export const useAuthStore = defineStore('employer-auth', () => {
  const user = ref<EmployerUser | null>(null)
  const memberships = ref<OrgMembership[]>([])
  const activeOrgId = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  const activeMembership = computed(() =>
    memberships.value.find(m => m.organization.id === activeOrgId.value) ?? null,
  )
  const org = computed(() => activeMembership.value?.organization ?? null)
  const role = computed(() => activeMembership.value?.role ?? null)

  /** Viewers may read everything but change nothing. */
  const canManageShifts = computed(() => role.value !== null && role.value !== 'viewer')
  /** Only owners and admins move money. */
  const canManageBilling = computed(() => role.value === 'owner' || role.value === 'admin')

  async function login(email: string, password: string) {
    loading.value = true
    error.value = null
    try {
      const result = await employerApi.login(email, password)
      user.value = result.user
      memberships.value = result.memberships
      // One company: skip the picker entirely.
      if (result.memberships.length === 1) {
        await selectOrg(result.memberships[0]!.organization.id)
      }
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : 'Could not sign in.'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Step 1: ask for a code. Nothing is created until it comes back verified,
   * so a typo'd address never becomes a company in the ops queue.
   */
  async function signupStart(input: EmployerSignup) {
    loading.value = true
    error.value = null
    try {
      await employerApi.signupStart(input)
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : 'Could not send the code.'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Step 2: redeem the code, which creates the company. They are signed in
   * immediately, but their org is `onboarding` until ops approves it, so the
   * console must show that state rather than letting them hit 403s on every
   * button.
   */
  async function signup(input: EmployerSignup) {
    loading.value = true
    error.value = null
    try {
      const result = await employerApi.signup(input)
      user.value = result.user
      memberships.value = result.memberships
      if (result.memberships.length === 1) {
        await selectOrg(result.memberships[0]!.organization.id)
      }
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : 'Could not create the account.'
      return false
    } finally {
      loading.value = false
    }
  }

  /** A company awaiting approval can read but not act. */
  const isPending = computed(() => org.value?.status === 'onboarding')

  async function selectOrg(orgId: string) {
    await employerApi.selectOrg(orgId)
    activeOrgId.value = orgId
  }

  /** Restore a session from a stored token on boot; see the worker store. */
  async function restore() {
    if (!employerApi.hasStoredSession()) return false
    try {
      const result = await employerApi.getMe()
      user.value = result.user
      memberships.value = result.memberships
      if (result.memberships.length === 1) {
        await selectOrg(result.memberships[0]!.organization.id)
      }
      return true
    } catch {
      await logout()
      return false
    }
  }

  async function logout() {
    await employerApi.logout()
    user.value = null
    memberships.value = []
    activeOrgId.value = null
  }

  return {
    user, memberships, activeOrgId, loading, error,
    isAuthenticated, activeMembership, org, role, canManageShifts, canManageBilling,
    isPending,
    login, signupStart, signup, selectOrg, restore, logout,
  }
})
